import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationService, TagEvent, TagPusherService } from '@libs/shared-services';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { TranslateModule } from '@ngx-translate/core';
import { ImagingCategoryToRoute, ImagingCategoryTranslations, ImagingGalleryComponent, ImagingGalleryItem } from '@libs/imaging';
import { routes, Loadable, DataState } from '@libs/utils';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { ImagingCategoryEnum } from '@ffd/api-client';
import { Router } from '@angular/router';
import { ToothDetailsImages } from './tooth-details-imaging.vm';

@Component({
  selector: 'ffd-tooth-details-imaging',
  imports: [CommonModule, SvgIconComponent, TranslateModule, ImagingGalleryComponent, LottieComponent],
  templateUrl: './tooth-details-imaging.component.html',
  styleUrls: ['./tooth-details-imaging.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ToothDetailsImagingComponent {
  private readonly navigationService = inject(NavigationService);
  private readonly router = inject(Router);
  private readonly tagPusherService = inject(TagPusherService);

  protected readonly ImagingCategoryTranslations = ImagingCategoryTranslations;

  @Input() public images: Loadable<ToothDetailsImages[]> = {
    data: [],
    state: DataState.Initial,
  };
  @Input() public siteId: string = '';
  @Input() public patientId: string = '';
  @Input() public hasImagingSoftware: Loadable<boolean> = {
    data: false,
    state: DataState.Loading,
  };
  @Input() public isCollapsed: boolean = false;
  @Input() public opacity: number = 1;
  @Output() public collapseClick: EventEmitter<void> = new EventEmitter<void>();

  protected TagEvent = TagEvent;

  public DataState = DataState;
  public readonly lottieOptions: AnimationOptions = {
    path: '/assets/animations/loading-spinner.json',
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  public onNavigationClick(image?: ImagingGalleryItem): void {
    if (image) {
      this.tagPusherService.pushTag({
        event: TagEvent.ChartingToothDetailsNavigation,
        target: 'digital',
        patient_id: this.patientId,
      });
      if (image.category !== ImagingCategoryEnum.CT && image.category !== ImagingCategoryEnum._3D) {
        this.navigationService.navigate(
          'imageOnSiteForPatientInImagingCategory',
          false,
          [
            this.siteId,
            this.patientId,
            ImagingCategoryToRoute[image.category],
            image.groupImages ? image.groupImages[0].id : image.id,
            image.inDateGroup ? image.date : undefined,
            image.teethGroup ?? undefined,
          ],
          { from: routes.charting },
        );
      }
      if (image.category === ImagingCategoryEnum.CT || image.category === ImagingCategoryEnum._3D) {
        const route = image.category === ImagingCategoryEnum.CT ? routes.cbct : routes.three_d;
        this.router.navigate([routes.site, this.siteId, routes.patients, this.patientId, routes.imaging, routes.scans, route], {
          queryParams: { studyId: image.id },
        });
      }
    }
  }

  public onCollapseClick(): void {
    this.collapseClick.emit();
  }
}
